import React, { useState, useEffect, useCallback } from "react";
import { Card, Tabs, Select, Button, Table, Empty, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import OrgFacilityFilter from "./OrgFacilityFilter";
import {
  getAllStateList,
  getSubToolbox,
  getAllMemoListing,
  getAllCompetencies,
  getToolboxTypesWithSubEducation,
  getMonthlyEducationRegister,
  getToolboxEducationRegister,
  getMemoRegisterList,
  getCompetencyRegisterList,
  getAllEquipmentData,
} from "./educationApi";

const { TabPane } = Tabs;
const { Option } = Select;

// Confirmed by reading the old app's registers/*/index.js files directly.
// Four of the five tabs (Education, Memo, Toolbox, Competency) share one
// shape: Org -> Facility(multi) -> State -> a type-specific dropdown, POSTed
// together, returning [{facility, user_id, user_name, education:[{name,
// attendedOn}]}] — pivoted client-side into Facility/Staff Name + one
// column per item name. Equipment is the odd one out: no type dropdown, and
// a FLAT (non-pivoted) response with its own fixed column set.
//
// Known gap: the old app also offers a PDF export (jsPDF + autotable) next
// to Excel on every tab here — only Excel is wired up (same xlsx/file-saver
// libraries and pattern already used elsewhere in this codebase, e.g.
// cleaningAudit/TrackingReport.js).
const pivotColumns = (rows) => {
  const names = new Set();
  rows.forEach((r) => (r.education || []).forEach((e) => names.add(e.name)));
  return [
    { title: "Facility", dataIndex: "facility", key: "facility", fixed: "left" },
    { title: "Staff Name", dataIndex: "user_name", key: "user_name", fixed: "left" },
    ...Array.from(names).map((name) => ({
      title: name,
      key: name,
      render: (_, row) => (row.education || []).find((e) => e.name === name)?.attendedOn || "-",
    })),
  ];
};

const exportPivotedExcel = (rows, filename) => {
  const columns = pivotColumns(rows);
  const header = columns.map((c) => c.title);
  const data = rows.map((row) => columns.map((c) => (c.dataIndex ? row[c.dataIndex] : c.render(null, row))));
  const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, filename);
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

const PivotedRegisterTab = ({ typeLabel, typeOptionsFetcher, typeOptionKey, typeOptionLabel, typeRequired, listFn, exportName }) => {
  const [states, setStates] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [orgId, setOrgId] = useState(null);
  const [facilityId, setFacilityId] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [typeId, setTypeId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      const [statesRes, typesRes] = await Promise.all([getAllStateList(), typeOptionsFetcher()]);
      setStates(!statesRes.error && Array.isArray(statesRes.result?.result) ? statesRes.result.result : []);
      setTypeOptions(!typesRes.error && Array.isArray(typesRes.result?.result) ? typesRes.result.result : []);
    })();
  }, [typeOptionsFetcher]);

  const search = useCallback(async () => {
    if (!orgId) {
      message.error("Please select a group first.");
      return;
    }
    if (typeRequired && !typeId) {
      message.error(`Select a ${typeLabel.toLowerCase()} first.`);
      return;
    }
    setLoading(true);
    setSearched(true);
    const { error, result } = await listFn({
      org_id: orgId,
      facility_id: facilityId.join(","),
      state_id: stateId,
      [typeOptionKey]: typeId,
    });
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [orgId, facilityId, stateId, typeId, typeRequired, typeLabel, typeOptionKey, listFn]);

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <OrgFacilityFilter orgId={orgId} onOrgChange={setOrgId} facilityId={facilityId} onFacilityChange={setFacilityId} facilityMode="multiple" />
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
          <Select allowClear showSearch optionFilterProp="children" placeholder="Select state" style={{ width: 180 }} value={stateId} onChange={setStateId}>
            {states.map((s) => <Option key={s.state_id ?? s.id} value={s.state_id ?? s.id}>{s.state_name ?? s.name}</Option>)}
          </Select>
        </div>
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>{typeLabel}</div>
          <Select allowClear={!typeRequired} showSearch optionFilterProp="children" placeholder={`Select ${typeLabel.toLowerCase()}`} style={{ width: 200 }} value={typeId} onChange={setTypeId}>
            {typeOptions.map((t) => <Option key={t.id} value={t.id}>{t[typeOptionLabel] ?? t.name ?? t.title}</Option>)}
          </Select>
        </div>
        <Button type="primary" loading={loading} onClick={search}>Search</Button>
        {rows.length > 0 && (
          <Button icon={<DownloadOutlined />} onClick={() => exportPivotedExcel(rows, exportName)}>Excel</Button>
        )}
      </div>

      {searched && (
        rows.length === 0 ? (
          <Card><Empty description="No register data for these filters." /></Card>
        ) : (
          <Table rowKey={(r, i) => r.user_id ?? i} columns={pivotColumns(rows)} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} scroll={{ x: true }} />
        )
      )}
    </div>
  );
};

// Toolbox's own dropdown flat-maps every sub-item across all toolbox groups
// (not just the first group's, unlike Manage Education's own Toolbox
// Education tab) — confirmed by reading registers/toolbox/index.js directly.
const getFlatSubToolboxList = async () => {
  const { error, result } = await getToolboxTypesWithSubEducation();
  if (error) return { error, result };
  const flat = (result?.result || []).flatMap((group) => group.sub_arr || []);
  return { error: false, result: { result: flat } };
};

export const EquipmentRegisterTab = () => {
  const [orgId, setOrgId] = useState(null);
  const [facilityId, setFacilityId] = useState([]);
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    (async () => {
      const { error, result } = await getAllStateList();
      setStates(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const search = useCallback(async () => {
    if (!orgId) {
      message.error("Please select a group first.");
      return;
    }
    setLoading(true);
    setSearched(true);
    const { error, result } = await getAllEquipmentData({ org_id: orgId, facility_id: facilityId.join(","), state_id: stateId });
    setRows(!error && Array.isArray(result?.result) ? result.result : []);
    setLoading(false);
  }, [orgId, facilityId, stateId]);

  const columns = [
    { title: "Equipment", dataIndex: "equipment", key: "equipment" },
    { title: "Facility", dataIndex: "facility_name", key: "facility_name" },
    { title: "State", dataIndex: "state_name", key: "state_name" },
    { title: "Serial Number", dataIndex: "serial_no", key: "serial_no" },
    { title: "Test Tag Number", dataIndex: "test_tag_no", key: "test_tag_no" },
    { title: "Test Tag Date", dataIndex: "test_tag_date", key: "test_tag_date" },
    { title: "Expiry Date", dataIndex: "expiry_date", key: "expiry_date" },
  ];

  const exportExcel = () => {
    const header = columns.map((c) => c.title);
    const data = rows.map((r) => columns.map((c) => r[c.dataIndex]));
    const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EQUIPMENT REGISTER");
    XLSX.writeFile(wb, "EQUIPMENT_REGISTER.xlsx");
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <OrgFacilityFilter orgId={orgId} onOrgChange={setOrgId} facilityId={facilityId} onFacilityChange={setFacilityId} facilityMode="multiple" />
        <div>
          <div style={{ marginBottom: 4, fontSize: 13, color: "#5B6472" }}>State</div>
          <Select allowClear showSearch optionFilterProp="children" placeholder="Select state" style={{ width: 180 }} value={stateId} onChange={setStateId}>
            {states.map((s) => <Option key={s.state_id ?? s.id} value={s.state_id ?? s.id}>{s.state_name ?? s.name}</Option>)}
          </Select>
        </div>
        <Button type="primary" loading={loading} onClick={search}>Search</Button>
        {rows.length > 0 && <Button icon={<DownloadOutlined />} onClick={exportExcel}>Excel</Button>}
      </div>

      {searched && (
        rows.length === 0 ? (
          <Card><Empty description="No equipment register data for these filters." /></Card>
        ) : (
          <Table rowKey={(r, i) => i} columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 20 }} scroll={{ x: true }} />
        )
      )}
    </div>
  );
};

// Each register on its own, so Employee Connect can show it under its subject
// (Education, Memos, Toolbox, Competency). Same props as the tabs below.
export const EducationRegister = () => (
  <PivotedRegisterTab typeLabel="Education" typeOptionsFetcher={() => getSubToolbox(2)} typeOptionKey="toolbox_id" typeOptionLabel="title" listFn={getMonthlyEducationRegister} exportName="EDUCATION REGISTER" />
);
export const MemoRegister = () => (
  <PivotedRegisterTab typeLabel="Memo" typeOptionsFetcher={getAllMemoListing} typeOptionKey="memo_id" typeOptionLabel="memo_name" listFn={getMemoRegisterList} exportName="MEMO REGISTER" />
);
export const ToolboxRegister = () => (
  <PivotedRegisterTab typeLabel="Toolbox" typeOptionsFetcher={getFlatSubToolboxList} typeOptionKey="sub_toolbox_id" typeOptionLabel="name" typeRequired listFn={getToolboxEducationRegister} exportName="TOOLBOX REGISTER" />
);
export const CompetencyRegister = () => (
  <PivotedRegisterTab typeLabel="Competency" typeOptionsFetcher={getAllCompetencies} typeOptionKey="competency_id" typeOptionLabel="title" listFn={getCompetencyRegisterList} exportName="COMPETENCY REGISTER" />
);

const Registers = () => (
  <Card bodyStyle={{ padding: "24px 28px" }}>
    <Tabs defaultActiveKey="education">
      <TabPane tab="Education" key="education">
        <PivotedRegisterTab
          typeLabel="Education"
          typeOptionsFetcher={() => getSubToolbox(2)}
          typeOptionKey="toolbox_id"
          typeOptionLabel="title"
          listFn={getMonthlyEducationRegister}
          exportName="EDUCATION REGISTER"
        />
      </TabPane>
      <TabPane tab="Equipment" key="equipment">
        <EquipmentRegisterTab />
      </TabPane>
      <TabPane tab="Memo" key="memo">
        <PivotedRegisterTab
          typeLabel="Memo"
          typeOptionsFetcher={getAllMemoListing}
          typeOptionKey="memo_id"
          typeOptionLabel="memo_name"
          listFn={getMemoRegisterList}
          exportName="MEMO REGISTER"
        />
      </TabPane>
      <TabPane tab="Toolbox" key="toolbox">
        <PivotedRegisterTab
          typeLabel="Toolbox"
          typeOptionsFetcher={getFlatSubToolboxList}
          typeOptionKey="sub_toolbox_id"
          typeOptionLabel="name"
          typeRequired
          listFn={getToolboxEducationRegister}
          exportName="TOOLBOX REGISTER"
        />
      </TabPane>
      <TabPane tab="Competency" key="competency">
        <PivotedRegisterTab
          typeLabel="Competency"
          typeOptionsFetcher={getAllCompetencies}
          typeOptionKey="competency_id"
          typeOptionLabel="title"
          listFn={getCompetencyRegisterList}
          exportName="COMPETENCY REGISTER"
        />
      </TabPane>
    </Tabs>
  </Card>
);

export default Registers;
